// x-image-zoom.js

/*
This script:
- Creates zoom-functionality for any img with class "zoomable" or image-slide img.
- Uses native <dialog> for zoomed state.
- Sets alt=default and compressed image sizes based on zoom level.
- Adds prev/next navigation in dialog for image-slide.
- Touchscreen swipe (left/right) navigates slides when at scale 1.
- Enables deep zoom for image-slide with "deep-zoomable" class. (Single img.zoomable always gets deep zoom.)
- NOTE: Touchscreen pinch zoom unsupported to avoid higher complexity.

Note about image size:
- If image has very high dimension/resolution (e.g. 11646 x 8734px = 100mill px) then the brower tab hangs on deep zoom,
  probably cause it is so big decoded in RAM (also image.decode() hangs, so this seems like a hard limitation
  that can't be solved by some async method either). May be less problem on Mac (supposedly better / optimized memory 
  architecture for this) and other browsers than Chrome
- E.g. half the dimension, like 50mill px seems ok (e.g. 10522px x 4474px, probably recommend a little
  less than that even and to use WebP or AVIF if image is large in filesize too)
*/

export default function xImageZoom() {

	// Images with class zoomable or all images in image slide
	const zoomableImages = document.querySelectorAll("figure img.zoomable, .image-slide figure img");

	if (!zoomableImages.length) return;

	const isEnglish = document.documentElement.lang === "en";

	// Text labels
	const openZoomLabel = isEnglish ? "Open zoom view" : "Åpne zoom-visning";
	const closeZoomLabel = isEnglish ? "Close zoom view" : "Lukk zoom-visning";
	const zoomInLabel = isEnglish ? "Zoom in" : "Zoom inn";
	const zoomOutLabel = isEnglish ? "Zoom out" : "Zoom ut";
	const prevLabel = isEnglish ? "Previous" : "Forrige";
	const nextLabel = isEnglish ? "Next" : "Neste";

	// Zoom limits
	const MIN_SCALE = 1;
	const MAX_SCALE = 6;
	const ZOOM_STEP = 1;

	// Swipe detection
	const SWIPE_THRESHOLD = 50;
	let swipeStartX = 0;
	let swipeStartY = 0;
	let isSwiping = false;

	// State variables
	let activeDialog = null;
	let activeImage = null;
	let activeOpenZoomBtn = null;
	let originalParent = null;
	let currentScale = MIN_SCALE;
	let translateX = 0;
	let translateY = 0;
	let isDragging = false;
	let dragStart = { x: 0, y: 0, tx: 0, ty: 0 };

	// Mode detection
	let isSlideMode = false;
	let canDeepZoom = false;
	let slideImages = [];
	let slideIndex = -1;

	// Create dialog element
	const dialog = document.createElement("dialog");
	dialog.className = "zoom-dialog";

	const dialogContent = document.createElement("div");
	dialogContent.className = "zoom-dialog-content";

	const closeBtn = document.createElement("button");
	closeBtn.className = "close-zoom-btn";
	closeBtn.setAttribute("aria-label", closeZoomLabel);

	// Controls container
	const controls = document.createElement("div");
	controls.className = "zoom-controls";

	// Navigation buttons (for image-slide)
	const btnPrev = document.createElement("button");
	btnPrev.className = "prev-next-btn prev";
	btnPrev.setAttribute("aria-label", prevLabel);

	const btnNext = document.createElement("button");
	btnNext.className = "prev-next-btn next";
	btnNext.setAttribute("aria-label", nextLabel);

	// Zoom buttons for deep-zoom
	const btnOut = document.createElement("button");
	btnOut.className = "zoom-btn zoom-deep-out";
	btnOut.setAttribute("aria-label", zoomOutLabel);

	const btnIn = document.createElement("button");
	btnIn.className = "zoom-btn zoom-deep-in";
	btnIn.setAttribute("aria-label", zoomInLabel);

	// Assemble controls: prev, out, in, next
	controls.appendChild(btnPrev);
	controls.appendChild(btnOut);
	controls.appendChild(btnIn);
	controls.appendChild(btnNext);

	dialogContent.appendChild(closeBtn);
	dialogContent.appendChild(controls);
	dialog.appendChild(dialogContent);
	document.body.appendChild(dialog);

	// Dialog close event
	dialog.addEventListener("close", cleanupZoom);
	closeBtn.addEventListener("click", () => dialog.close());

	// Zoom button handlers
	btnIn.addEventListener("click", (e) => {
		e.stopPropagation();
		if (canDeepZoom && currentScale < MAX_SCALE) {
			changeZoom(ZOOM_STEP);
		}
	});

	btnOut.addEventListener("click", (e) => {
		e.stopPropagation();
		if (canDeepZoom && currentScale > MIN_SCALE) {
			changeZoom(-ZOOM_STEP);
		}
	});

	// Slide button handlers
	btnPrev.addEventListener("click", (e) => {
		e.stopPropagation();
		if (isSlideMode) navigateSlide(-1);
	});

	btnNext.addEventListener("click", (e) => {
		e.stopPropagation();
		if (isSlideMode) navigateSlide(1);
	});

	// Drag handlers
	document.addEventListener("mousemove", handleDrag);
	document.addEventListener("mouseup", endDrag);
	document.addEventListener("touchmove", handleDrag, { passive: false });
	document.addEventListener("touchend", endDrag);

	// Keyboard controls
	document.addEventListener("keydown", (e) => {
		if (!activeDialog) return;

		// Close on Escape
		if (e.key === "Escape") {
			dialog.close();
			return;
		}

		// When zoomed > 1: arrow keys always pan (standalone or slide mode with deep zoom)
		if (currentScale > MIN_SCALE && ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) {
			if (canDeepZoom) {
				e.preventDefault();
				const step = 50;
				if (e.key === "ArrowUp") translateY += step;
				if (e.key === "ArrowDown") translateY -= step;
				if (e.key === "ArrowLeft") translateX += step;
				if (e.key === "ArrowRight") translateX -= step;
				constrainPan();
				applyTransform();
			}
			return;
		}

		// When at scale 1 in slide mode: arrow keys navigate prev/next
		if (isSlideMode && currentScale === MIN_SCALE && (e.key === "ArrowLeft" || e.key === "ArrowRight")) {
			e.preventDefault();
			navigateSlide(e.key === "ArrowLeft" ? -1 : 1);
			return;
		}

		// Zoom in/out with +/- keys
		if (!canDeepZoom) return;

		if (e.key === "+" || e.key === "=") {
			e.preventDefault();
			if (currentScale < MAX_SCALE) changeZoom(ZOOM_STEP);
		} else if (e.key === "-" || e.key === "_") {
			e.preventDefault();
			if (currentScale > MIN_SCALE) changeZoom(-ZOOM_STEP);
		}
	});

	// Initialize zoomable images
	zoomableImages.forEach((image) => {
		const figure = image.closest("figure");
		if (!figure) return;

		// Create zoom button on each figure
		const openZoomBtn = document.createElement("button");
		openZoomBtn.className = "open-zoom-btn";
		openZoomBtn.setAttribute("aria-label", openZoomLabel);
		figure.appendChild(openZoomBtn);

		openZoomBtn.addEventListener("click", (e) => {
			e.stopPropagation();
			openZoom(image, openZoomBtn);
		});
	});

	// Helper: Load image variant (default/compressed)
	function loadVariant(image, variant) {
		const imageUrl = new URL(image.src);
		if (imageUrl.searchParams.get("alt") === variant) return;

		imageUrl.searchParams.set("alt", variant);
		dialog.classList.add("image-loading");
		image.src = imageUrl.toString();

		const cleanup = () => dialog.classList.remove("image-loading");
		image.addEventListener("load", cleanup, { once: true });
		image.addEventListener("error", cleanup, { once: true });
	}

	function openZoom(image, openZoomBtn) {
		activeDialog = dialog;
		activeImage = image;
		activeOpenZoomBtn = openZoomBtn;
		originalParent = image.parentNode;
		currentScale = MIN_SCALE;
		translateX = 0;
		translateY = 0;
		isDragging = false;
		isSwiping = false;

		// Detect mode based on slide container
		const slideContainer = image.closest(".image-slide");
		isSlideMode = !!slideContainer;
		canDeepZoom = isSlideMode ? slideContainer.classList.contains("deep-zoomable") : true;

		// Apply CSS classes
		if (isSlideMode) {
			dialog.classList.add("is-slide-mode");
			slideImages = Array.from(slideContainer.querySelectorAll("figure img"));
			slideIndex = slideImages.indexOf(image);
			updateNavButtons();
		}
		if (canDeepZoom) {
			dialog.classList.add("has-deep-zoom");
		}

		dialog.classList.add("is-pre-zoom");

		// Move image to dialog
		dialogContent.insertBefore(image, controls);
		image.classList.add("has-zoomed");

		// Add drag listeners if deep zoom is enabled
		if (canDeepZoom) {
			image.addEventListener("mousedown", startDrag);
			image.addEventListener("touchstart", startDrag, { passive: false });
		}

		// Add swipe listener for slide navigation (scale 1 only)
		if (isSlideMode) {
			image.addEventListener("touchstart", handleSwipeStart, { passive: true });
		}

		// Load default image variant for modal view
		loadVariant(image, "default");

		dialog.showModal();
		updateZoomButtons();
	}

	// Navigate slides for image-slide
	function navigateSlide(direction) {
		const newIndex = slideIndex + direction;
		if (newIndex < 0 || newIndex >= slideImages.length) return;

		// Return current image to original parent
		if (activeImage && originalParent) {
			cleanupImage();
			originalParent.prepend(activeImage);
		}

		// Update to new image
		slideIndex = newIndex;
		const newImage = slideImages[slideIndex];
		const newFigure = newImage.closest("figure");

		activeImage = newImage;
		originalParent = newImage.parentNode;
		activeOpenZoomBtn = newFigure.querySelector(".open-zoom-btn") || activeOpenZoomBtn;

		// Reset interaction state (scale/position already at defaults via changeZoom or initial state)
		isDragging = false;
		isSwiping = false;

		// Move to dialog
		dialogContent.insertBefore(newImage, controls);
		newImage.classList.add("has-zoomed");

		// Add drag listeners if deep zoom
		if (canDeepZoom) {
			newImage.addEventListener("mousedown", startDrag);
			newImage.addEventListener("touchstart", startDrag, { passive: false });
		}

		// Add swipe listener for slide navigation
		if (isSlideMode) {
			newImage.addEventListener("touchstart", handleSwipeStart, { passive: true });
		}

		// Load default image variant for modal view
		loadVariant(newImage, "default");

		// Ensure CSS reflects pre-zoom state
		dialog.classList.add("is-pre-zoom");
		dialog.classList.remove("is-deep-zoomed");

		updateNavButtons();
		updateZoomButtons();
	}

	function changeZoom(delta) {
		if (!canDeepZoom) return;

		const oldScale = currentScale;
		currentScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, currentScale + delta));

		// Load compressed variant on first deep zoom
		if (currentScale > MIN_SCALE && oldScale === MIN_SCALE && delta > 0) {
			loadVariant(activeImage, "compressed");
		}

		if (currentScale === MIN_SCALE) {
			translateX = 0;
			translateY = 0;
			dialog.classList.add("is-pre-zoom");
			dialog.classList.remove("is-deep-zoomed");
		} else {
			constrainPan();
			dialog.classList.remove("is-pre-zoom");
			dialog.classList.add("is-deep-zoomed");
		}

		applyTransform();
		updateZoomButtons();
	}

	function updateNavButtons() {
		if (!isSlideMode) return;
		btnPrev.disabled = slideIndex <= 0;
		btnNext.disabled = slideIndex >= slideImages.length - 1;
	}

	function updateZoomButtons() {
		if (!canDeepZoom) return;
		btnIn.disabled = currentScale >= MAX_SCALE;
		btnOut.disabled = currentScale <= MIN_SCALE;
	}

	function applyTransform() {
		if (!activeImage) return;
		activeImage.style.transform = `translate(${translateX}px, ${translateY}px) scale(${currentScale})`;
	}

	// Constrain pan: Keep drag within image boundaries
	function constrainPan() {
		if (!activeImage || currentScale <= MIN_SCALE) {
			translateX = 0;
			translateY = 0;
			return;
		}

		const viewportWidth = dialog.clientWidth;
		const viewportHeight = dialog.clientHeight;
		const imageWidth = activeImage.naturalWidth;
		const imageHeight = activeImage.naturalHeight;

		if (!imageWidth || !imageHeight) return;

		// Calculate max pan based on zoomed image size vs viewport
		const fitScale = Math.min(viewportWidth / imageWidth, viewportHeight / imageHeight);
		const displayedWidth = imageWidth * fitScale * currentScale;
		const displayedHeight = imageHeight * fitScale * currentScale;
		const maxX = Math.max(0, (displayedWidth - viewportWidth) / 2);
		const maxY = Math.max(0, (displayedHeight - viewportHeight) / 2);

		translateX = Math.max(-maxX, Math.min(maxX, translateX));
		translateY = Math.max(-maxY, Math.min(maxY, translateY));
	}

	function startDrag(e) {
		if (!canDeepZoom || currentScale <= MIN_SCALE) return;
		if (e.target.closest("button")) return;

		e.preventDefault();
		isDragging = true;
		dialog.classList.add("is-dragging");

		const clientX = e.touches ? e.touches[0].clientX : e.clientX;
		const clientY = e.touches ? e.touches[0].clientY : e.clientY;

		dragStart = {
			x: clientX,
			y: clientY,
			tx: translateX,
			ty: translateY
		};
	}

	function handleDrag(e) {
		if (!isDragging || !activeDialog) return;
		if (isSwiping) return; // Don't drag if we're swiping

		e.preventDefault();

		const clientX = e.touches ? e.touches[0].clientX : e.clientX;
		const clientY = e.touches ? e.touches[0].clientY : e.clientY;

		translateX = dragStart.tx + (clientX - dragStart.x);
		translateY = dragStart.ty + (clientY - dragStart.y);

		constrainPan();
		applyTransform();
	}

	// Touch swipe handling for slide navigation (scale 1 only)
	function handleSwipeStart(e) {
		// Only handle single touch at scale 1 in slide mode
		if (!isSlideMode || currentScale !== MIN_SCALE || e.touches.length !== 1) return;

		swipeStartX = e.touches[0].clientX;
		swipeStartY = e.touches[0].clientY;
		isSwiping = true;
	}

	function endDrag(e) {
		// Handle swipe end first (only for touch events with changedTouches)
		if (isSwiping && e.changedTouches && e.changedTouches.length > 0) {
			const touchEndX = e.changedTouches[0].clientX;
			const touchEndY = e.changedTouches[0].clientY;
			const dx = touchEndX - swipeStartX;
			const dy = touchEndY - swipeStartY;

			// Horizontal swipe detected (must be more horizontal than vertical)
			if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
				// Right swipe (positive dx) -> Previous, Left swipe (negative dx) -> Next
				navigateSlide(dx > 0 ? -1 : 1);
			}

			isSwiping = false;
			e.preventDefault(); // Prevent click after swipe
			return;
		}

		// Handle drag end
		if (!isDragging) return;
		isDragging = false;
		if (activeDialog) dialog.classList.remove("is-dragging");
	}

	function cleanupImage() {
		if (!activeImage) return;
		activeImage.style.transform = "";
		if (canDeepZoom) {
			activeImage.removeEventListener("mousedown", startDrag);
			activeImage.removeEventListener("touchstart", startDrag);
		}
		if (isSlideMode) {
			activeImage.removeEventListener("touchstart", handleSwipeStart);
		}
	}

	function cleanupZoom() {
		if (!activeDialog) return;

		const triggerButton = activeOpenZoomBtn;

		if (activeImage && originalParent) {
			cleanupImage();
			originalParent.prepend(activeImage);
		}

		// Remove all state classes
		dialog.classList.remove(
			"is-slide-mode",
			"has-deep-zoom",
			"is-deep-zoomed",
			"is-pre-zoom",
			"is-dragging",
			"image-loading"
		);

		// Reset state
		activeDialog = null;
		activeImage = null;
		activeOpenZoomBtn = null;
		originalParent = null;
		currentScale = MIN_SCALE;
		translateX = 0;
		translateY = 0;
		isDragging = false;
		isSwiping = false;
		isSlideMode = false;
		canDeepZoom = false;
		slideImages = [];
		slideIndex = -1;

		if (triggerButton) {
			triggerButton.focus();
		}
	}
}
